import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { FileText, Upload, X, Check, Camera } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const serviceOptions = [
  { id: "leer", label: "Leerbehandeling (va €80)" },
  { id: "stoelen", label: "Dieptereiniging Stoffen Stoelen (va €70)" },
  { id: "interieur", label: "Complete Interieurreiniging (va €100)" },
  { id: "ramen", label: "Ramencoating (va €79)" },
  { id: "anders", label: "Anders / Op Maat" },
];

const budgetRanges = [
  "€50 - €100",
  "€100 - €200",
  "€200 - €350",
  "€350 - €500",
  "€500+",
  "Nog niet bekend",
];

export default function QuotePage() {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);

  const [formData, setFormData] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    vehicle_type: "",
    vehicle_brand: "",
    vehicle_year: "",
    services_requested: [],
    description: "",
    preferred_date: "",
    budget_range: "",
  });

  const handleServiceToggle = (serviceId) => {
    setFormData((prev) => ({
      ...prev,
      services_requested: prev.services_requested.includes(serviceId)
        ? prev.services_requested.filter((id) => id !== serviceId)
        : [...prev.services_requested, serviceId],
    }));
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (photos.length + files.length > 5) {
      toast.error("Maximaal 5 foto's toegestaan");
      return;
    }

    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        const response = await axios.post(`${API}/upload-photo`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setPhotos((prev) => [...prev, { url: response.data.url, name: file.name }]);
      }
      toast.success("Foto's geüpload");
    } catch (error) {
      console.error("Error uploading photo:", error);
      toast.error("Fout bij uploaden van foto");
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = (index) => {
    setPhotos((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.services_requested.length === 0) {
      toast.error("Selecteer minimaal één dienst");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, {
        ...formData,
        photos: photos.map((p) => p.url),
      });
      setSubmitted(true);
      toast.success("Offerte aanvraag verzonden!");
    } catch (error) {
      console.error("Error submitting quote:", error);
      toast.error("Er is iets misgegaan. Probeer het opnieuw.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="quote-success" className="pt-20 min-h-screen bg-ug-black flex items-center justify-center">
        <div className="max-w-md mx-auto px-4 text-center">
          <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-8">
            <Check className="w-10 h-10 text-primary" />
          </div>
          <h1 className="text-3xl font-heading font-black uppercase tracking-tighter text-white mb-4">
            Aanvraag Ontvangen!
          </h1>
          <p className="text-gray-400 mb-8">
            Bedankt voor uw aanvraag. Wij nemen binnen 24 uur contact met u op met een passende offerte.
          </p>
          <Button
            onClick={() => window.location.reload()}
            className="bg-primary hover:bg-primary-hover text-white rounded-none uppercase tracking-widest font-bold px-8 py-4"
          >
            Nieuwe Aanvraag
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="quote-page" className="pt-20">
      {/* Hero */}
      <section className="py-16 bg-ug-paper">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <span className="text-primary text-sm font-bold tracking-widest uppercase">
              Offerte Aanvragen
            </span>
            <h1 className="text-4xl sm:text-5xl font-heading font-black uppercase tracking-tighter text-white mt-4">
              Pakket Op Maat
            </h1>
            <p className="text-gray-400 text-lg mt-6">
              Vertel ons over uw auto en wensen. Wij stellen een passend pakket samen met een vrijblijvende offerte.
            </p>
          </div>
        </div>
      </section>

      {/* Quote Form */}
      <section className="py-16 bg-ug-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Contact Info */}
            <div className="bg-ug-paper border border-white/5 p-6 space-y-6">
              <div className="flex items-center mb-2">
                <FileText className="w-5 h-5 text-primary mr-3" />
                <h3 className="text-lg font-heading font-bold uppercase tracking-wide text-white">
                  Uw Gegevens
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-white">Naam *</Label>
                  <Input
                    id="name"
                    data-testid="quote-name"
                    value={formData.customer_name}
                    onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                    required
                    className="bg-ug-black border-white/10 text-white rounded-none h-12 placeholder:text-gray-600"
                    placeholder="Uw volledige naam"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-white">Telefoonnummer *</Label>
                  <Input
                    id="phone"
                    data-testid="quote-phone"
                    type="tel"
                    value={formData.customer_phone}
                    onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                    required
                    className="bg-ug-black border-white/10 text-white rounded-none h-12 placeholder:text-gray-600"
                    placeholder="06 12345678"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-white">E-mailadres *</Label>
                <Input
                  id="email"
                  data-testid="quote-email"
                  type="email"
                  value={formData.customer_email}
                  onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                  required
                  className="bg-ug-black border-white/10 text-white rounded-none h-12 placeholder:text-gray-600"
                  placeholder="uw@email.nl"
                />
              </div>
            </div>

            {/* Vehicle Info */}
            <div className="bg-ug-paper border border-white/5 p-6 space-y-6">
              <h3 className="text-lg font-heading font-bold uppercase tracking-wide text-white">
                Voertuig Informatie
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="vehicle_type" className="text-white">Voertuig Type *</Label>
                  <Select
                    onValueChange={(value) => setFormData({ ...formData, vehicle_type: value })}
                    required
                  >
                    <SelectTrigger
                      data-testid="quote-vehicle-type"
                      className="bg-ug-black border-white/10 text-white rounded-none h-12"
                    >
                      <SelectValue placeholder="Selecteer type" />
                    </SelectTrigger>
                    <SelectContent className="bg-ug-black border-white/10">
                      {["Sedan", "SUV", "Hatchback", "Stationwagen", "Coupé", "Cabrio", "Bus"].map(
                        (type) => (
                          <SelectItem key={type} value={type} className="text-white hover:bg-white/10">
                            {type}
                          </SelectItem>
                        )
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vehicle_brand" className="text-white">Merk & Model *</Label>
                  <Input
                    id="vehicle_brand"
                    data-testid="quote-vehicle-brand"
                    value={formData.vehicle_brand}
                    onChange={(e) => setFormData({ ...formData, vehicle_brand: e.target.value })}
                    required
                    className="bg-ug-black border-white/10 text-white rounded-none h-12 placeholder:text-gray-600"
                    placeholder="bijv. BMW 3-serie"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vehicle_year" className="text-white">Bouwjaar</Label>
                  <Input
                    id="vehicle_year"
                    data-testid="quote-vehicle-year"
                    value={formData.vehicle_year}
                    onChange={(e) => setFormData({ ...formData, vehicle_year: e.target.value })}
                    className="bg-ug-black border-white/10 text-white rounded-none h-12 placeholder:text-gray-600"
                    placeholder="bijv. 2020"
                  />
                </div>
              </div>
            </div>

            {/* Services Selection */}
            <div className="bg-ug-paper border border-white/5 p-6 space-y-6">
              <h3 className="text-lg font-heading font-bold uppercase tracking-wide text-white">
                Gewenste Diensten *
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {serviceOptions.map((service) => {
                  const isSelected = formData.services_requested.includes(service.id);
                  return (
                    <label
                      key={service.id}
                      data-testid={`service-checkbox-${service.id}`}
                      className={`flex items-center space-x-3 p-4 border cursor-pointer transition-colors duration-300 ${
                        isSelected
                          ? "border-primary bg-primary/10"
                          : "border-white/10 hover:border-white/30"
                      }`}
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => handleServiceToggle(service.id)}
                        className="border-white/30 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                      />
                      <span className="text-white">{service.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Description */}
            <div className="bg-ug-paper border border-white/5 p-6 space-y-6">
              <h3 className="text-lg font-heading font-bold uppercase tracking-wide text-white">
                Beschrijving & Wensen *
              </h3>
              <Textarea
                data-testid="quote-description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                className="bg-ug-black border-white/10 text-white rounded-none min-h-[150px] placeholder:text-gray-600"
                placeholder="Beschrijf de huidige staat van uw auto en eventuele specifieke wensen of probleemgebieden..."
              />
            </div>

            {/* Photo Upload */}
            <div className="bg-ug-paper border border-white/5 p-6 space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-heading font-bold uppercase tracking-wide text-white">
                  Foto's (Optioneel)
                </h3>
                <span className="text-gray-500 text-sm">{photos.length}/5</span>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {photos.map((photo, index) => (
                  <div key={index} className="relative aspect-square bg-ug-black border border-white/10">
                    <img
                      src={photo.url}
                      alt={photo.name}
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => removePhoto(index)}
                      className="absolute top-2 right-2 w-6 h-6 bg-red-500 flex items-center justify-center"
                    >
                      <X className="w-4 h-4 text-white" />
                    </button>
                  </div>
                ))}

                {photos.length < 5 && (
                  <label
                    data-testid="photo-upload"
                    className="aspect-square bg-ug-black border border-dashed border-white/20 hover:border-primary/50 flex flex-col items-center justify-center cursor-pointer transition-colors duration-300"
                  >
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handlePhotoUpload}
                      className="hidden"
                      disabled={uploading}
                    />
                    {uploading ? (
                      <div className="spinner w-6 h-6" />
                    ) : (
                      <>
                        <Camera className="w-6 h-6 text-gray-500 mb-2" />
                        <span className="text-xs text-gray-500">Upload</span>
                      </>
                    )}
                  </label>
                )}
              </div>
            </div>

            {/* Additional Options */}
            <div className="bg-ug-paper border border-white/5 p-6 space-y-6">
              <h3 className="text-lg font-heading font-bold uppercase tracking-wide text-white">
                Extra Informatie
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="preferred_date" className="text-white">Voorkeursdatum</Label>
                  <Input
                    id="preferred_date"
                    data-testid="quote-preferred-date"
                    type="date"
                    value={formData.preferred_date}
                    onChange={(e) => setFormData({ ...formData, preferred_date: e.target.value })}
                    className="bg-ug-black border-white/10 text-white rounded-none h-12"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="budget" className="text-white">Budget Indicatie</Label>
                  <Select
                    onValueChange={(value) => setFormData({ ...formData, budget_range: value })}
                  >
                    <SelectTrigger
                      data-testid="quote-budget"
                      className="bg-ug-black border-white/10 text-white rounded-none h-12"
                    >
                      <SelectValue placeholder="Selecteer budget" />
                    </SelectTrigger>
                    <SelectContent className="bg-ug-black border-white/10">
                      {budgetRanges.map((range) => (
                        <SelectItem key={range} value={range} className="text-white hover:bg-white/10">
                          {range}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <Button
              type="submit"
              data-testid="submit-quote"
              disabled={loading}
              className="w-full bg-primary hover:bg-primary-hover text-white rounded-none uppercase tracking-widest font-bold py-4 transition-colors duration-300 disabled:opacity-50"
            >
              {loading ? "Bezig met verzenden..." : "Offerte Aanvragen"}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
}
